import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { userApi } from '../../api';
import { queryKeys, cacheUtils, queryClient } from '../query-client';
import { useNotifications } from '../notifications';
import { useProtectedQuery } from '../use-protected-query';
import type { 
  User, 
  UserSearchParams, 
  CreateUserRequest, 
  UpdateUserRequest,
  PaginatedResponse 
} from '../../types';

// Hook for fetching paginated users list
export const useUsers = (params?: UserSearchParams) => {
  return useProtectedQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => userApi.getUsers(params),
    staleTime: 2 * 60 * 1000, // 2 minutes for lists (more frequent updates)
    placeholderData: (previousData: PaginatedResponse<User> | undefined) => previousData, // Keep previous data while loading
    // Enable background refetching for better UX
    refetchOnWindowFocus: true,
    // Prefetch next page for pagination
    select: (data: PaginatedResponse<User>) => {
      // If we have a next page, prefetch it
      if (data && params?.page && data.pages > params.page) {
        const nextPageParams = { ...params, page: params.page + 1 };
        setTimeout(() => {
          queryClient.prefetchQuery({
            queryKey: queryKeys.users.list(nextPageParams),
            queryFn: () => userApi.getUsers(nextPageParams),
            staleTime: 2 * 60 * 1000,
          });
        }, 100);
      }
      return data;
    },
  });
};

// Hook for fetching a single user
export const useUser = (id: string, enabled: boolean = true) => {
  return useProtectedQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: () => userApi.getUser(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual records
  });
};

// Hook for creating a new user
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notifications = useNotifications(t);
  
  return useMutation({
    mutationFn: (data: CreateUserRequest) => userApi.createUser(data),
    onMutate: async (newUser) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: queryKeys.users.lists() });
      
      // Snapshot the previous value
      const previousUsers = queryClient.getQueriesData({ queryKey: queryKeys.users.lists() });
      
      // Optimistically update to the new value
      queryClient.setQueriesData(
        { queryKey: queryKeys.users.lists() },
        (old: PaginatedResponse<User> | undefined) => {
          if (!old) return old;
          
          // Create optimistic user object
          const optimisticUser: User = {
            id: `temp-${Date.now()}`, // Temporary ID
            email: newUser.email,
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            isActive: newUser.isActive,
            isSuperuser: false,
            roles: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          
          return {
            ...old,
            items: [optimisticUser, ...old.items],
            total: old.total + 1,
          };
        }
      );
      
      return { previousUsers };
    },
    onError: (error, _newUser, context) => {
      // Rollback optimistic update on error
      if (context?.previousUsers) {
        context.previousUsers.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      notifications.operationError('userCreated', error);
    },
    onSuccess: (createdUser) => {
      // Replace optimistic update with real data
      queryClient.setQueriesData(
        { queryKey: queryKeys.users.lists() },
        (old: PaginatedResponse<User> | undefined) => {
          if (!old) return old;
          
          return {
            ...old,
            items: old.items.map(user => 
              user.id.startsWith('temp-') ? createdUser : user
            ),
          };
        }
      );
      
      // Cache the new user
      queryClient.setQueryData(queryKeys.users.detail(createdUser.id), createdUser);
      
      notifications.operationSuccess('userCreated');
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      cacheUtils.invalidateUsers();
    },
  });
};

// Hook for updating a user
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notifications = useNotifications(t);
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      userApi.updateUser(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.users.detail(id) });
      await queryClient.cancelQueries({ queryKey: queryKeys.users.lists() });
      
      // Snapshot the previous value
      const previousUser = queryClient.getQueryData(queryKeys.users.detail(id));
      const previousUsers = queryClient.getQueriesData({ queryKey: queryKeys.users.lists() });
      
      // Optimistically update the user
      if (previousUser) {
        const optimisticUser = { ...previousUser as User, ...data };
        
        queryClient.setQueryData(queryKeys.users.detail(id), optimisticUser);
        cacheUtils.updateUserInCache(id, (old: User) => ({ ...old, ...data }));
      }
      
      return { previousUser, previousUsers };
    },
    onError: (error, { id }, context) => {
      // Rollback optimistic updates on error
      if (context?.previousUser) {
        queryClient.setQueryData(queryKeys.users.detail(id), context.previousUser);
      }
      if (context?.previousUsers) {
        context.previousUsers.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      notifications.operationError('userUpdated', error);
    },
    onSuccess: (updatedUser, { id }) => {
      // Update cache with real data
      queryClient.setQueryData(queryKeys.users.detail(id), updatedUser);
      cacheUtils.updateUserInCache(id, () => updatedUser);
      
      notifications.operationSuccess('userUpdated');
    },
  });
};

// Hook for deleting a user
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notifications = useNotifications(t);
  
  return useMutation({
    mutationFn: (id: string) => userApi.deleteUser(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.users.detail(id) });
      await queryClient.cancelQueries({ queryKey: queryKeys.users.lists() });
      
      // Snapshot the previous values
      const previousUser = queryClient.getQueryData(queryKeys.users.detail(id));
      const previousUsers = queryClient.getQueriesData({ queryKey: queryKeys.users.lists() });
      
      // Optimistically remove the user
      cacheUtils.removeUserFromCache(id);
      
      return { previousUser, previousUsers };
    },
    onError: (error, id, context) => {
      // Rollback optimistic updates on error
      if (context?.previousUser) {
        queryClient.setQueryData(queryKeys.users.detail(id), context.previousUser);
      }
      if (context?.previousUsers) {
        context.previousUsers.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      notifications.operationError('userDeleted', error);
    },
    onSuccess: () => {
      notifications.operationSuccess('userDeleted');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      cacheUtils.invalidateUsers();
    },
  });
};

// Hook for assigning a role to a user
export const useAssignUserRole = () => {
  const { t } = useTranslation();
  const notifications = useNotifications(t);

  return useMutation({
    mutationFn: ({ userId, roleId, serviceName }: {
      userId: string;
      roleId: string;
      serviceName?: string;
    }) => userApi.assignRole(userId, roleId, serviceName),
    onSuccess: () => {
      // Invalidate all user lists to refetch with new roles
      // This ensures the user table shows updated role assignments
      cacheUtils.invalidateUsers();
      notifications.operationSuccess('roleAssigned');
    },
    onError: (error) => {
      notifications.operationError('roleAssigned', error);
    },
  });
};

// Hook for removing a role from a user
export const useRemoveUserRole = () => {
  const { t } = useTranslation();
  const notifications = useNotifications(t);

  return useMutation({
    mutationFn: ({ userId, roleId, serviceName }: {
      userId: string;
      roleId: string;
      serviceName?: string;
    }) => userApi.removeRole(userId, roleId, serviceName),
    onSuccess: () => {
      // Invalidate all user lists to refetch without the role
      // This ensures the user table shows updated role assignments
      cacheUtils.invalidateUsers();
      notifications.operationSuccess('roleRemoved');
    },
    onError: (error) => {
      notifications.operationError('roleRemoved', error);
    },
  });
};